import { useEffect, useMemo, useState } from "react";
import {
  formatTimeFrame,
  formatTimeToBrowserTime,
  getFovSnapshot,
  getLastSecThirdWords,
} from "../../utility/Utils";
import { useNavigate } from "react-router-dom";
import { Box } from "@mui/system";
import { Icon } from "@vds/icons";
import { ButtonIcon } from "@vds/button-icons";
import { TextLink } from "@vds/buttons";
import { Body, Title } from "@vds/typography";
import DisplayCanvas from "../../../polygonLib/displayPolygonLib/displayCanvas";
import NotificationBanner from "../../../layouts/NotificationBanner";
import { GreyText } from "../Modals/RoiModals/stylesRoiModal";
import CustomHOCTable from "./CustomHOCTable";
import { getFilterIcon, getSortingIcon } from "../../utility/tableUtils";
import { getFilterOptions } from "../../utility/deviceTableUtils";
import {
  base64ToBlobUrl,
  ImageDownloadMedia,
} from "../../../model/mediaService";
import { useSelector } from "react-redux";
import { Loader } from "@vds/loaders";
import { Tooltip } from "antd";
import moment from "moment";
import { Badge } from "@vds/badges";
import { isWithin24Hour } from "../../../utils";
import RoiImage from "../../../pages/BulkRule/RoiImage";
import { CVAPP_UNOBJECTDETECTION } from "../../constants/cvappConstants";
import { isEntryExitApp } from "../../utility/cvAppUtils";
import { PAGE_IDENTIFIERS } from "../../constants/tableConstants";
import { setDetailNavSource } from "../../../hooks/usePaginationReset";
import { LightText } from "../../../features/camerOnboarding/discoveredCameraOnboarding/styles";

const AlertsTableHOC = CustomHOCTable();

const AlertsTable = (props) => {
  const { activeSession } = useSelector(
    (state) => state.sessionReducer.activeSession
  );
  const pairedImages =
    useSelector((state) => state.imagesReducer.streamImages) || [];
  const { fovList } = useSelector((state) => state?.deviceReducer);
  const { ruleList, CreateAlertButton, setTableLoading } = props;
  const cvAppList = useSelector((state) => state.cvAppReducer.cvAppList) || [];
  const [filteredInfo, setFilteredInfo] = useState([]);
  const [showTableFilter, setShowTableFilter] = useState(true);
  const [showNotification, setShowNotification] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [ruleDataSource, setRuleDataSource] = useState(null);
  const [imageLoader, setImageLoader] = useState(false);

  const navigate = useNavigate();

  const columnDetails = useMemo(() => {
    return [
      {
        title: (
          <Title size="small" bold primitive="h2" viewport="desktop">
            Name
          </Title>
        ),
        dataIndex: "Name",
        key: "Name",
        filteredValue: filteredInfo.Name || null,
        onFilter: (value, record) =>
          record.Name.toLowerCase().includes(value.toLowerCase()),
        sortIcon: ({ sortOrder }) => getSortingIcon(sortOrder),
        sorter: (a, b) =>
          a?.Name?.toLowerCase() < b?.Name?.toLowerCase() ? -1 : 1,
        sortDirections: ["ascend", "descend"],
        ellipsis: true,
        render: (text, record) => {
          const linkUrl = "/alertDetails";
          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
                maxWidth: "100%",
              }}
            >
              {isWithin24Hour(record?.createdon ?? new Date()) && (
                <span style={{ flexShrink: 0 }}>
                  <Badge fillColor="black">New</Badge>
                </span>
              )}
              <Tooltip
                placement="bottomLeft"
                autoAdjustOverflow="false"
                title={text}
              >
                <TextLink
                  type="standAlone"
                  onClick={(e) => {
                    e.preventDefault();
                    setDetailNavSource(PAGE_IDENTIFIERS.ALERTS);
                    navigate(linkUrl, {
                      state: { record: record, mode: "edit" },
                    });
                  }}
                  style={{
                    marginLeft: "10px",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    width: "200px",
                    display: "block",
                    borderBottomStyle: "none",
                    overflow: "hidden",
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                >
                  <Body size="large" viewport="desktop" primitive="span">
                    {text}
                  </Body>
                </TextLink>
              </Tooltip>
            </div>
          );
        },
      },
      {
        title: (
          <Title size="small" bold primitive="h2" viewport="desktop">
            App
          </Title>
        ),
        isFilter: true,
        filter: getFilterOptions(ruleDataSource, "App"),
        dataIndex: "App",
        key: "App",
        customStyle: { maxWidth: "260px" },
        sortIcon: ({ sortOrder }) => getSortingIcon(sortOrder),
        sorter: (a, b) =>
          a?.App?.toLowerCase() < b?.App?.toLowerCase() ? -1 : 1,
        sortDirections: ["ascend", "descend"],
        filteredValue: filteredInfo.App || null,
        filterIcon: (filtered) => getFilterIcon(filtered),
        onFilter: (value, record) =>
          record.App.toLowerCase().includes(value.toLowerCase()),
      },
      {
        title: (
          <Title size="small" bold primitive="h2" viewport="desktop">
            Site
          </Title>
        ),
        dataIndex: "Site",
        key: "Site",
        customStyle: { maxWidth: "280px" },
        sortIcon: ({ sortOrder }) => getSortingIcon(sortOrder),
        sorter: (a, b) =>
          a?.Site?.toLowerCase() < b?.Site?.toLowerCase() ? -1 : 1,
        sortDirections: ["ascend", "descend"],
        filteredValue: filteredInfo.Site || null,
        isFilter: true,
        filter: getFilterOptions(ruleDataSource, "Site"),
        filterIcon: (filtered) => getFilterIcon(filtered),
        onFilter: (value, record) =>
          record.Site.toLowerCase().includes(value.toLowerCase()),
      },
      {
        title: (
          <Title size="small" bold primitive="h2" viewport="desktop">
            Camera
          </Title>
        ),
        dataIndex: "Camera",
        key: "Camera",
        customStyle: { maxWidth: "180px" },
        filteredValue: filteredInfo.Camera || null,
        isFilter: true,
        filter: getFilterOptions(ruleDataSource, "Camera"),
        onFilter: (value, record) =>
          record.Camera.toLowerCase().includes(value.toLowerCase()),
        sortIcon: ({ sortOrder }) => getSortingIcon(sortOrder),
        sorter: (a, b) =>
          a?.Camera?.toLowerCase() < b?.Camera?.toLowerCase() ? -1 : 1,
        sortDirections: ["ascend", "descend"],
      },
      {
        title: (
          <Title size="small" bold primitive="h2" viewport="desktop">
            Condition
          </Title>
        ),
        dataIndex: "Condition",
        key: "Condition",
        onFilter: (value, record) =>
          record.condition.toLowerCase().includes(value.toLowerCase()),
        render: (text, record) => {
          const timeFrameText = formatTimeFrame(
            formatTimeToBrowserTime(record?.starttime),
            formatTimeToBrowserTime(record?.endtime),
            record?.timeFrame
          );

          return (
            <div style={{ maxWidth: "241.5px" }}>
              <div style={{ display: "flex" }}>
                <Icon name="reports-and-alerts" size="20px" color="#A7A7A7" />
                <div style={{ marginLeft: "5px" }}>
                  <Body size="medium">{record?.condition}</Body>
                </div>
              </div>
              <br />
              <div style={{ display: "flex" }}>
                <Icon name="clock" size="20px" color="#A7A7A7" />
                <div style={{ marginLeft: "5px" }}>
                  <Body size="medium">
                    {record?.timeFrame === "Always active" ||
                    record?.timeFrame?.length == ""
                      ? "Always active"
                      : `${timeFrameText}`}
                  </Body>
                </div>
              </div>
            </div>
          );
        },
      },
    ];
  }, [ruleDataSource]);

  const onExpand = (expanded, record) => {
    setExpandedRowKeys(expanded ? [record.key] : []);
    if (!record?.aggregateRule) {
      fetchFloorImage(record);
    }
  };

  const [floorImage, setFloorImage] = useState(null);
  const [floorImageError, setFloorImageError] = useState(false);
  const videoSource = floorImage;
  const imageSize = { width: 168.23, height: 108 };

  const getRuleDataSource = (list) => {
    return list?.map((item) => {
      const matchedSessionData = cvAppList?.find(
        (sessionItem) => sessionItem?.name === item?.appName
      );
      const displayNameFull =
        matchedSessionData?.resources?.displayNameFull ||
        item?.appName ||
        "Unknown App";

      return {
        Name: item?.name,
        App: displayNameFull,
        Site: item.site,
        Camera: item.cameraName,
        Condition: item?.condition,
        Starttime: item?.starttime,
        Endtime: item?.endtime,
        TimeFrame: item?.showTimeFrame === true ? "RB2" : "RB1",
        id: item.id,
        roiData: item.roiData,
        roiPolygon: item.roiPolygon,
        ...item,
        key: item.id,
      };
    });
  };

  const fetchFloorImage = async (record) => {
    try {
      setImageLoader(true);
      setFloorImageError(false);
      const pairedImage =
        pairedImages && pairedImages.find((img) => img?.id === record?.fovid);
      if (pairedImage) {
        const imgUrl = pairedImage?.imageUrl;
        setFloorImage(imgUrl);
      } else {
        const imgUrl = await ImageDownloadMedia(
          getFovSnapshot(fovList, record?.fovid),
          activeSession,
          "",
          "snapshot"
        );
        setFloorImage(imgUrl);
      }
    } catch (error) {
      setFloorImage(null);
      setFloorImageError(true);
      console.error("Error downloading image:", error);
    } finally {
      setImageLoader(false);
    }
  };

  const getRuleTableData = () => {
    let ruleData = getRuleDataSource(ruleList);
    setRuleDataSource(ruleData);
  };

  useEffect(() => {
    getRuleTableData();
  }, [ruleList]);

  return (
    <>
      {showNotification && (
        <div
          style={{
            position: "fixed",
            top: 0,
            zIndex: 1050,
          }}
        >
          <NotificationBanner
            notificationInfo={{
              type: "No Permission",
              priority: "critical",
            }}
            setShowNotification={setShowNotification}
          />
        </div>
      )}
      <Box
        sx={{
          borderBottomLeftRadius: 5,
          borderBottomRightRadius: 5,
          borderTopWidth: 0,
        }}
      >
        <AlertsTableHOC
          columns={columnDetails}
          showSearch={true}
          loading={props?.tableLoading}
          searchTextPlaceholder={"Search alerts"}
          extraAction={CreateAlertButton}
          dataList={ruleDataSource || []}
          expandable={{
            expandedRowRender: (record) =>
              record?.aggregateRule ||
              isEntryExitApp(cvAppList, record?.serviceid) ? (
                <div style={{ marginLeft: "50px" }}>
                  <GreyText>Regions of Interest</GreyText>
                  <RoiImage
                    ruleList={record}
                    activeSession={activeSession}
                    fovList={fovList}
                    cvAppList={cvAppList}
                  />
                </div>
              ) : (
                <div style={{ marginLeft: "50px" }}>
                  <GreyText>Region of Interest</GreyText>
                  {imageLoader ? (
                    <div className="alert-snapshot-loading">
                        <div className="camera-snapshot-loader-wrapper">
                          <Loader active={true} fullscreen={false} surface="light" />
                        </div>
                      </div>
                  ) : floorImageError ? (
                    <div className="alert-snapshot-error">
                      <div className="camera-snapshot-error-content">
                        <Icon name="error" size="small" color="#6f7171" />
                        <LightText>Snapshot failed to load</LightText>
                        <ButtonIcon
                          onClick={() => fetchFloorImage(record)}
                          renderIcon={() => <Icon name="sync" size="small" />}
                        />
                      </div>
                    </div>
                  ) : (
                    <DisplayCanvas
                      videoSource={videoSource}
                      imageSize={imageSize}
                      initialPolygons={record?.roiPolygon}
                      originalSize={[record?.roiData, record?.roiData]}
                    />
                  )}
                </div>
              ),
          }}
          rowKey="key"
          expandedRowKeys={expandedRowKeys}
          onExpand={onExpand}
          pageIdentifier={PAGE_IDENTIFIERS.ALERTS}
          useReduxPersistence={true}
        />
      </Box>
    </>
  );
};

export default AlertsTable;
